import { useCoverGeniusSeeMoreCoversState } from "../useCoverGeniusSeeMoreCoversState";
import { useEffect, useMemo, useRef } from "react";
import type { QuestionProps } from "../../components/Questionnaire/Section/Question/types";
import { isQuestion } from "../../components/Questionnaire/Section/Question/types";
import {
  useQuestionnaireActions,
  useVisibilityMap,
} from "../../features/questionnaire";
import { useSnowplowContext } from "@simplybusiness/services";

export const getVisibleQuestions = (allQuestions: QuestionProps[]) =>
  allQuestions.filter(isQuestion).filter(({ visible }) => visible);

export const getSuggestedQuestions = (allQuestions: QuestionProps[]) =>
  allQuestions.filter(isQuestion).filter(({ suggested }) => suggested);

/**
 * Hook to manage CoverGenius "see more covers" functionality.
 *
 * This hook manages question visibility through questionnaire actions
 * based on the "see more" state:
 * - When in limited view: only suggested questions are visible
 * - When expanded: all visible questions are shown
 *
 * The initial "see more" state is computed during questionnaire hydration
 * (via computeInitialQuestionnaireState), so the first render already
 * has the correct visibility. This effect only handles reactive updates
 * when the user toggles "see more" or when the visibility map changes.
 */
export const useCoverGenius = ({
  questions,
}: {
  questions: QuestionProps[];
}) => {
  const [showOnlySuggested, setShowOnlySuggested] =
    useCoverGeniusSeeMoreCoversState();
  const { setVisible, setHidden } = useQuestionnaireActions();
  const { snowplow } = useSnowplowContext();
  const visibilityMap = useVisibilityMap();
  const trackedQuestions = useRef(new Set<string>());
  const hasTrackedInitialCovers = useRef(false);

  const suggestedQuestions = useMemo(
    () => getSuggestedQuestions(questions),
    [questions],
  );

  const visibleQuestions = useMemo(
    () => getVisibleQuestions(questions),
    [questions],
  );

  // Derive whether we should be in "see more" mode by checking membership,
  // not just counts — a suggested-but-invisible question would inflate the
  // suggested count and break a length-only comparison.
  const suggestedNames = useMemo(
    () => new Set(suggestedQuestions.map(q => q.name)),
    [suggestedQuestions],
  );

  const shouldShowOnlySuggested = useMemo(
    () =>
      suggestedQuestions.length > 0 &&
      visibleQuestions.some(q => !suggestedNames.has(q.name)),
    [suggestedQuestions.length, visibleQuestions, suggestedNames],
  );

  // Sync atom state on mount so other components (CoverGeniusSection) read the correct value
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current) {
      setShowOnlySuggested(shouldShowOnlySuggested);
      hasInitialized.current = true;
    }
  }, [shouldShowOnlySuggested, setShowOnlySuggested]);

  // On first render, use the synchronously-computed value to avoid a flash
  // of all covers before the init effect sets the atom. This is safe because
  // the init effect sets the atom to `shouldShowOnlySuggested`, so when
  // `hasInitialized` flips to true the atom already holds the same value
  // and the effect deps don't change.
  const isShowingSuggested = hasInitialized.current
    ? showOnlySuggested
    : shouldShowOnlySuggested;

  useEffect(() => {
    const questionNames = isShowingSuggested
      ? suggestedQuestions.map(q => q.name)
      : visibleQuestions.map(q => q.name);

    setVisible(questionNames);

    // Only hide non-suggested primary questions when showing suggested only
    // A question is primary if it has display.type === "personalised_cover"
    if (isShowingSuggested) {
      const toHide = visibleQuestions
        .filter(q => !suggestedNames.has(q.name))
        .filter(q => q.display?.type === "personalised_cover")
        .map(q => q.name);

      if (toHide.length > 0) {
        setHidden(toHide);
      }
    }
  }, [
    isShowingSuggested,
    suggestedQuestions,
    suggestedNames,
    visibleQuestions,
    visibilityMap,
    setVisible,
    setHidden,
  ]);

  // Track coverPresented events for newly visible covers
  // Strategy:
  // 1. On initial mount: ONLY track suggested covers that are visible (suggested=true AND visible=true)
  // 2. On subsequent updates when "see more" is expanded: Track additional non-suggested visible covers
  useEffect(() => {
    const visibleNamesSet = new Set(visibleQuestions.map(q => q.name));

    // On first render, ONLY track visible suggested covers
    if (!hasTrackedInitialCovers.current) {
      suggestedQuestions.forEach(question => {
        if (visibleNamesSet.has(question.name)) {
          snowplow?.trigger("coverPresented", {
            property: question.primaryText || question.name,
          });
          trackedQuestions.current.add(question.name);
        }
      });

      hasTrackedInitialCovers.current = true;
      return; // Exit early on first render - don't track non-suggested covers
    }

    // On subsequent renders, if expanded view, track non-suggested visible covers
    if (!showOnlySuggested) {
      const suggestedNamesSet = new Set(suggestedQuestions.map(q => q.name));

      visibleQuestions.forEach(question => {
        if (
          !suggestedNamesSet.has(question.name) &&
          !trackedQuestions.current.has(question.name)
        ) {
          snowplow?.trigger("coverPresented", {
            property: question.primaryText || question.name,
          });
          trackedQuestions.current.add(question.name);
        }
      });
    }
  }, [showOnlySuggested, suggestedQuestions, visibleQuestions, snowplow]);
};
